import type { WithT } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { APIKey } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { TableColumnHeader, Time, Tooltip } from '@feathr/components';
import { TimeFormat } from '@feathr/hooks';

import APIKeyOptionsV2 from './APIKeyOptionsV2';
import { isFullGrant, summarizeScopes } from './APIKeysPage.utils';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

/**
 * Read-only V2 column set. Deliberately renders only the user-given name in the
 * "Key" column — the raw key/secret value is never displayed (security
 * requirement of #8549). There is no key/secret column, unlike the V1 table.
 */
function apiKeyColumnsV2({ t }: WithT): Array<IColumn<APIKey>> {
  return [
    {
      id: 'service_name',
      Header: TableColumnHeader({ title: t('Key') }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      Cell({ original }): JSX.Element {
        return <Observer>{(): JSX.Element => <>{original.name}</>}</Observer>;
      },
    },
    {
      id: 'expires_at',
      Header: TableColumnHeader({ title: t('Expires on') }),
      sortable: false,
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      // Full date per the design annotation ("July 22, 2026") — a deliberate
      // divergence from THEME.md's shortDate table convention.
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => {
              const expiresAt = original.get('expires_at');
              if (!expiresAt) {
                return <>{t('Never')}</>;
              }
              return <Time format={TimeFormat.fullDate} timestamp={expiresAt} />;
            }}
          </Observer>
        );
      },
    },
    {
      id: 'created',
      Header: TableColumnHeader({ title: t('Created on') }),
      sortable: false,
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      // dateCreated derives from the ObjectId, so it is always present — no
      // "Never" fallback needed.
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => (
              <Time format={TimeFormat.fullDate} timestamp={original.dateCreated} />
            )}
          </Observer>
        );
      },
    },
    {
      id: 'last_used_at',
      Header: TableColumnHeader({ title: t('Last used on') }),
      sortable: false,
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      // Relative time per design ("30+ days ago" style), not a calendar date.
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => {
              const lastUsed = original.get('last_used_at');
              if (!lastUsed) {
                return <>{t('Never')}</>;
              }
              return <Time format={TimeFormat.timeFromNowUtcDay} timestamp={lastUsed} />;
            }}
          </Observer>
        );
      },
    },
    {
      id: 'scopes',
      Header: TableColumnHeader({ title: t('Scope') }),
      sortable: false,
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => {
              const scopes = original.get('scopes');
              const summary = summarizeScopes(scopes, t);
              // "All" and the em dash render as-is; a partial grant renders a
              // bare count, which the design annotates with a tooltip breaking
              // the scopes down.
              if (!scopes?.length || isFullGrant(scopes)) {
                return <>{summary}</>;
              }
              return <Tooltip title={scopes.join(', ')}>{summary}</Tooltip>;
            }}
          </Observer>
        );
      },
    },
    {
      id: 'options',
      Header: TableColumnHeader({ title: t('Options') }),
      width: 80,
      sortable: false,
      className: tableStyles.cellCenter,
      Cell({ original }): JSX.Element {
        return <APIKeyOptionsV2 apiKey={original} />;
      },
    },
  ];
}

export default apiKeyColumnsV2;
